"use client";

import { joinClassNames } from "@utils/helpers";
import { usePrevious } from "@utils/hooks";
import Link from "next/link";
import { useState } from "react";
import { motion } from "framer-motion";
import { SparklesIcon } from "@heroicons/react/20/solid";
import { useIsShopsite } from "@context/DomainContext";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";

interface Submenu {
  name: string;
  slug: string;
  as?: string;
  isNew?: boolean;
}

interface Props {
  submenus: Submenu[];
  topValue?: number;
  hrefPrefix?: string;
}

/**
 * The submenu component is mainly used for navigation on a route segment. Example, in the account page has a route of "/account", in account page
 * route segments such as "/order-history", "/my-tickets" etc. This just gives users the ability to navigate these route segments with ease.
 *
 */
export default function Submenu({
  submenus,
  topValue,
  hrefPrefix = "",
}: Props) {
  const pathname = usePathname();
  const t = useTranslations("Submenu");
  const [hoveredSubmenu, setHoveredSubmenu] = useState<{
    height: number;
    y: number;
  } | null>(null);
  const lastHovered = usePrevious(hoveredSubmenu, false).current;
  const isShopsite = useIsShopsite();
  const current_slug = pathname.replace(hrefPrefix, "") || "/";
  const current = submenus.find(
    (submenu) =>
      submenu.slug === current_slug ||
      (submenu.slug !== "/" &&
        (current_slug.startsWith(submenu.slug) ||
          current_slug.startsWith(submenu.as || "n/a")))
  );

  const textColor = isShopsite
    ? "text-gray-400"
    : "text-[color:var(--gray-a11)]";
  const hoverTextColor = isShopsite
    ? "hover:text-indigo-600"
    : "hover:text-[color:var(--accent-a11)]";
  const activeTextColor = isShopsite
    ? "text-indigo-500"
    : "text-[color:var(--accent-a11)]";
  const hoverBgColor = isShopsite
    ? "bg-indigo-50"
    : "bg-[color:var(--accent-a3)]";

  return (
    <div
      style={{
        top: topValue ? `${topValue + 10}px` : "6.5rem",
      }}
      className="hidden lg:block col-span-1 w-full sticky h-min mt-0"
    >
      <div className="relative flex flex-1 flex-col">
        {submenus.map((submenu, index) => (
          <Link
            key={submenu.name}
            id={submenu.name}
            href={`${hrefPrefix}${submenu.slug}`}
            onMouseEnter={() => {
              const height =
                document.getElementById(submenu.name)?.getBoundingClientRect()
                  .height ?? 0;
              let y = 0;
              for (let i = 0; i < index; ++i) {
                y +=
                  document
                    .getElementById(submenus[i].name)
                    ?.getBoundingClientRect().height ?? 0;
              }
              setHoveredSubmenu({ height, y });
            }}
            onMouseLeave={() => setHoveredSubmenu(null)}
            className={joinClassNames(
              "flex items-center justify-between relative z-10 px-4 py-2 font-medium transition-colors ease-in-out duration-200",
              hoverTextColor,
              current?.slug === submenu.slug ? activeTextColor : textColor
            )}
          >
            {submenu.name}
            {submenu.isNew && (
              <span
                className={joinClassNames(
                  "inline-flex items-center gap-1 px-1 py-0.5 text-sm font-semibold max-h-6",
                  isShopsite
                    ? "bg-indigo-100 rounded-lg"
                    : "bg-[color:var(--accent-a3)] rounded-[var(--radius-3)]"
                )}
              >
                <SparklesIcon
                  className={joinClassNames(
                    "w-4 h-4 animate-shimmer",
                    isShopsite
                      ? "text-indigo-800"
                      : "text-[color:var(--accent-a11)] "
                  )}
                />
                <span
                  className={
                    isShopsite
                      ? "text-indigo-800"
                      : "text-[color:var(--accent-a11)]"
                  }
                >
                  {t("new")
                    .split("")
                    .map((char, index) => (
                      <span
                        key={index}
                        className="animate-shimmer"
                        style={{ animationDelay: `${50 * (index + 1)}ms` }}
                      >
                        {char}
                      </span>
                    ))}
                </span>
              </span>
            )}
          </Link>
        ))}
        <motion.span
          animate={
            hoveredSubmenu
              ? {
                  height: hoveredSubmenu.height,
                  y: hoveredSubmenu.y,
                  opacity: 1,
                }
              : {
                  height: lastHovered?.height,
                  y: lastHovered?.y,
                  opacity: 0,
                  transition: { opacity: { duration: 0.5 } },
                }
          }
          transition={{ ease: "easeInOut", duration: 0.2 }}
          className={joinClassNames(
            "absolute w-full top-0 pointer-events-none",
            hoverBgColor,
            isShopsite ? "rounded-lg" : "rounded-[var(--radius-3)]"
          )}
        />
      </div>
    </div>
  );
}
